// BookAclassic client analytics wrapper.
//
// Usage:
//   import { track, identify, page, reset } from "@/lib/analytics";
//   track("car_viewed", { car_id: 42, value: 120, currency: "GBP" });
//
// Fan-out order: PostHog (product analytics + replay) and GA4 (marketing,
// ads, ecommerce). Neither SDK is loaded until the user gives consent.

import type posthog from "posthog-js";
import type { EventName, EventProps, IdentifyTraits } from "./events";
import { getConsent, hasChosen, subscribeConsent, type ConsentState } from "./consent";
import { captureFirstTouch, getFirstTouch, getCurrentUtm } from "./utm";
import { isProductionAnalyticsEnabled } from "./environment";
import { isAutomatedBrowser } from "./automation";
import type { AnalyticsReleaseReason } from "./startup";
import { sanitizeCheckoutUrl } from "@/lib/checkout-state";
import { getActivePlatform } from "@/lib/platform/config";
import { bookingAnalyticsContextFromSdkId, type BookingAnalyticsContext } from "@workspace/booking-rules";

type PostHog = typeof posthog;

const BASE = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");

function redactUrlLikeValue(value: unknown): unknown {
  if (typeof value !== "string") return value;
  if (!value.includes("/checkout")) return value;
  return sanitizeCheckoutUrl(value);
}

function redactAnalyticsProps<T extends Record<string, unknown>>(props: T): T {
  const out: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(props)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      out[name] = redactAnalyticsProps(value as Record<string, unknown>);
    } else if (/url|location|referrer|landing_path/i.test(name)) {
      out[name] = redactUrlLikeValue(value);
    } else {
      out[name] = value;
    }
  }
  return out as T;
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;
const PH_API_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;

// Paid-ads pixels — pre-wired but inert until configured. Both are
// marketing/ads tools, so they load ONLY when marketing consent is
// granted AND their respective IDs are set. With no IDs configured
// (today's state) nothing loads and nothing fires.
//   - VITE_META_PIXEL_ID            → Meta Pixel id (numeric string)
//   - VITE_GOOGLE_ADS_ID            → Google Ads conversion id ("AW-XXXX")
//   - VITE_GOOGLE_ADS_*_LABEL       → per-conversion labels; a conversion
//                                     only fires when its label is set.
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
const GOOGLE_ADS_ID = import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined;
const GOOGLE_ADS_BEGIN_CHECKOUT_LABEL = import.meta.env
  .VITE_GOOGLE_ADS_BEGIN_CHECKOUT_LABEL as string | undefined;
const GOOGLE_ADS_PURCHASE_LABEL = import.meta.env
  .VITE_GOOGLE_ADS_PURCHASE_LABEL as string | undefined;
// Task #7874 — two additional Ads conversion actions:
//   - payment label   → PRIMARY conversion, fires on `payment_succeeded`
//     (renter reached a genuinely-captured paid outcome).
//   - booking-request label → SECONDARY conversion, fires on booking-request
//     submission on BOTH paths (`booking_requested` legacy POST /bookings and
//     `checkout_session_created` on the PFC checkout path).
const GOOGLE_ADS_PAYMENT_LABEL = import.meta.env
  .VITE_GOOGLE_ADS_PAYMENT_LABEL as string | undefined;
const GOOGLE_ADS_BOOKING_REQUEST_LABEL = import.meta.env
  .VITE_GOOGLE_ADS_BOOKING_REQUEST_LABEL as string | undefined;

// Client SDKs point at our own reverse proxy so ad-blockers and ITP
// can't strip 20–40% of events. See `/api/_a/*` in the API server.
// `window` is guarded so importing this module in a non-DOM environment
// (vitest node-env suites transitively pulling it in) can't throw at
// module-evaluation time and crash the whole test run.
const ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "";
const PH_PROXY_HOST = `${ORIGIN}${BASE}/api/_a/ph`;
const GA_PROXY_HOST = `${ORIGIN}${BASE}/api/_a/g`;

type AnalyticsState = {
  initialized: boolean;
  phLoaded: boolean;
  gaLoaded: boolean;
  gtagScriptLoaded: boolean;
  metaLoaded: boolean;
  googleAdsLoaded: boolean;
  posthog: PostHog | null;
  userId: string | null;
  pendingPosthogReset: boolean;
  traits: IdentifyTraits | null;
  queue: Array<() => void>;
  // The most recent page_view requested before GA4 finished configuring.
  // gtag.js drops events that arrive before `gtag("config", ...)`, so we
  // hold the landing view here and replay it once loadGA4() has run. See
  // page() / loadGA4() below.
  pendingGaPageView: { path: string; title: string; location: string } | null;
  releaseReason: AnalyticsReleaseReason;
};

const state: AnalyticsState = {
  initialized: false,
  phLoaded: false,
  gaLoaded: false,
  gtagScriptLoaded: false,
  metaLoaded: false,
  googleAdsLoaded: false,
  posthog: null,
  userId: null,
  pendingPosthogReset: false,
  traits: null,
  queue: [],
  pendingGaPageView: null,
  releaseReason: "interaction",
};

type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

const ROUTES_WITH_REPLAY = [
  /^\/[a-z]{2}(\/[a-z]{2})?\/(cars\/|checkout|login|signup)/,
  /^\/car\//,
  "/checkout",
  "/login",
  "/signup",
  "/dashboard/listings/new",
  /\/dashboard\/listings\/\d+\/resume/,
];

function pathEligibleForReplay(path: string): boolean {
  return ROUTES_WITH_REPLAY.some((r) =>
    typeof r === "string" ? path === r || path.startsWith(r + "/") : r.test(path),
  );
}

function startCurrentConsentedReplay(
  instance: Pick<NonNullable<typeof state.posthog>, "startSessionRecording">,
  path = window.location.pathname,
) {
  if (state.releaseReason !== "dwell" && getConsent().analytics &&
      pathEligibleForReplay(path)) instance.startSessionRecording();
}

// gtag.js only recognises commands pushed to `dataLayer` as the native
// `arguments` object (that is why Google's canonical snippet is literally
// `function gtag(){dataLayer.push(arguments)}`). A real Array — which a
// spread/rest parameter produces — is treated as a GTM dataLayer *event*
// and silently ignored, so `config`/`event`/`consent` never reach GA4 and
// zero `/g/collect` hits fire. The rest param keeps the call signature
// typed; we push `arguments` (not `args`) so the commands are recognised.
function gtag(..._args: unknown[]) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

function emitGaPageView(path: string, title: string, location: string) {
  try {
    window.gtag?.("event", "page_view", {
      page_path: path,
      page_title: title,
      page_location: location,
    });
  } catch {}
}

function pushConsentMode(consent: ConsentState) {
  const analytics = consent.analytics ? "granted" : "denied";
  const marketing = consent.marketing ? "granted" : "denied";
  gtag("consent", "update", {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    functionality_storage: "granted",
    security_storage: "granted",
  });
}

function ensureConsentModeDefault() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // Must push the native `arguments` object — see the note on `gtag()`
  // above. A real Array here makes gtag.js ignore every command, which is
  // what zeroed out GA4 collection in production.
  window.gtag =
    window.gtag ||
    function gtagShim() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  // Default = denied for everything except essential. This is the
  // Consent Mode v2 contract, fires exactly once before any gtag call.
  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
}

async function loadPostHog(consent: ConsentState) {
  if (!isProductionAnalyticsEnabled()) return;
  if (isAutomatedBrowser()) return;
  if (!PH_API_KEY || state.phLoaded) return;
  // Product decision: PostHog autocaptures pageviews even before the
  // cookie banner is dismissed. To stay GDPR-safe in that pre-consent
  // window we boot PostHog with `persistence: "memory"` — no cookie,
  // no localStorage entry, just an in-tab distinct_id that vanishes
  // on reload. Once the user grants analytics consent (handled in
  // `applyConsent`) we upgrade persistence to localStorage+cookie via
  // `set_config`, and once they explicitly reject we call
  // `opt_out_capturing`. If the user has explicitly rejected before
  // the SDK ever loaded, skip loading entirely.
  if (hasChosen() && !consent.analytics) return;
  try {
    const mod = await import("posthog-js");
    // A rejection while the dynamic import was pending wins.
    const currentConsent = getActivePlatform() === "bac" ? getConsent() : consent;
    if (getActivePlatform() === "bac" && hasChosen() && !currentConsent.analytics) return;
    const fullConsent = currentConsent.analytics;
    const ph = mod.default;
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    ph.init(PH_API_KEY, {
      api_host: PH_PROXY_HOST,
      ui_host: "https://eu.posthog.com",
      // Use PostHog's native pageview tracking so the default dashboards
      // (DAUs/WAUs, Growth accounting, Retention, Pageview funnel) and
      // scroll-depth tracking populate. Scroll-depth properties piggy-back
      // on `$pageleave`, which fires automatically when `capture_pageview`
      // is on and `capture_pageleave` is `true` / `'if_capture_pageview'`.
      capture_pageview: true,
      capture_pageleave: "if_capture_pageview",
      // Scroll-depth percentage rides on $pageleave but is only recorded
      // when heatmaps capture is enabled. Without this PostHog's
      // "Scroll depth" web-analytics check stays at 0% and the heatmaps
      // tab is empty even though pageviews flow.
      capture_heatmaps: true,
      autocapture: true,
      persistence: fullConsent ? "localStorage+cookie" : "memory",
      disable_session_recording:
        state.releaseReason === "dwell" ||
        !fullConsent ||
        !pathEligibleForReplay(path),
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-ph-mask], input[type="password"], input[name*="card"], input[name*="cvc"], input[name*="phone"], input[name*="address"]',
      },
      before_send: (event) => {
        if (event?.properties) {
          event.properties = redactAnalyticsProps(event.properties);
        }
        return event;
      },
      // Volume/cost control: only record on the routes in
      // ROUTES_WITH_REPLAY (~ car pages / checkout / auth / listing-wizard) AND only
      // 100% of those eligible sessions for now. We toggle start/stopSessionRecording per
      // route in `page()` so non-eligible pages never sample.
      ...({ session_recording_sample_rate: 1, disable_session_recording_on_load: true } as Record<string, unknown>),
      loaded: (instance) => {
        // Identifying a user before consent links pre-consent
        // pageviews to a known account, which we don't want. Wait
        // until applyConsent() upgrades us before calling identify.
        if (state.userId && (getActivePlatform() === "bac" ? getConsent().analytics : fullConsent)) {
          identifyPostHog(instance, state.userId, state.traits ?? undefined);
        }
        // The first page event often precedes this lazy SDK import. Route
        // hooks could not start a recorder that did not exist yet.
        startCurrentConsentedReplay(instance);
      },
    });
    state.posthog = ph;
    state.phLoaded = true;
  } catch (err) {
    console.warn("[analytics] failed to load posthog-js", err);
  }
}

// gtag.js is a single shared library used by both GA4 and Google Ads.
// Load it at most once (through our proxy so adblockers can't strip it)
// regardless of which product triggers it first.
function ensureGtagScript(id: string) {
  if (state.gtagScriptLoaded) return;
  const s = document.createElement("script");
  s.async = true;
  s.src = `${GA_PROXY_HOST}/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);
  gtag("js", new Date());
  state.gtagScriptLoaded = true;
}

function loadGA4(_consent: ConsentState) {
  if (!isProductionAnalyticsEnabled()) return;
  if (!GA_MEASUREMENT_ID || state.gaLoaded) return;
  // GA4 runs in Consent Mode v2 ADVANCED: gtag is configured immediately,
  // even before the visitor makes a consent choice. While consent is denied
  // (the default set in ensureConsentModeDefault, kept current by
  // pushConsentMode), GA4 sends only anonymous, COOKIELESS measurement pings
  // — no client_id cookie, no ad-storage — which lets it count live users /
  // traffic pre-consent the same way PostHog does in memory mode. Once the
  // visitor grants analytics/marketing, pushConsentMode flips storage to
  // "granted" and full (cookie-backed) measurement resumes. Google Ads and
  // Meta Pixel stay fully consent-gated below; only GA4 measurement is
  // advanced. No early return on consent — that was the old "basic" mode
  // that left GA4 blank for visitors who never accepted the banner.
  ensureGtagScript(GA_MEASUREMENT_ID);
  gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    transport_url: GA_PROXY_HOST,
    first_party_collection: true,
    ...(state.userId ? { user_id: state.userId } : {}),
  });
  state.gaLoaded = true;
  // Replay the landing page_view. On initial load GA4 config is delayed
  // behind the async consent flow (and the dynamic posthog-js import it
  // awaits), so the page() call fired at mount runs BEFORE this config and
  // would be dropped by gtag.js (events before `config` have no stream).
  // Landing-only sessions never fire a second route-change page_view, so
  // without this replay GA4 receives zero hits for them. We emit the view
  // page() stashed (or synthesize one for the current page) AFTER config so
  // it always reaches GA. `send_page_view: false` above means there is no
  // automatic page_view to double-count.
  const pv = state.pendingGaPageView ?? {
    path: window.location.pathname,
    title: typeof document !== "undefined" ? document.title : "",
    location: sanitizeCheckoutUrl(window.location.href),
  };
  state.pendingGaPageView = null;
  emitGaPageView(pv.path, pv.title, pv.location);
}

// Google Ads conversion tag. Marketing-only: ad-storage/ad-personalization
// pings are additionally gated by Consent Mode v2 (see pushConsentMode), so
// withdrawal stops the conversions automatically. Inert until VITE_GOOGLE_ADS_ID
// is set.
function loadGoogleAds(consent: ConsentState) {
  if (!isProductionAnalyticsEnabled()) return;
  if (!GOOGLE_ADS_ID) return;
  if (!state.googleAdsLoaded) {
    // Configure the Ads destination alongside GA4 on page load. Consent Mode's
    // denied default keeps this cookieless until marketing consent is granted,
    // while ensuring a later ad_storage grant can set the Ads click cookie.
    ensureGtagScript(GOOGLE_ADS_ID);
    gtag("config", GOOGLE_ADS_ID, { allow_enhanced_conversions: true });
    state.googleAdsLoaded = true;
  }
  // Enhanced-conversion user data remains marketing-consent gated.
  if (consent.marketing) maybeSetAdsUserData();
}

// Meta Pixel (fbq). Marketing-only. Unlike Google, Meta has no Consent Mode,
// so we only init after marketing consent is granted and explicitly
// grant/revoke its consent signal in applyConsent on withdrawal. Inert until
// VITE_META_PIXEL_ID is set.
function loadMetaPixel(consent: ConsentState) {
  if (!isProductionAnalyticsEnabled()) return;
  if (!META_PIXEL_ID || state.metaLoaded) return;
  if (!consent.marketing) return;
  if (!window.fbq) {
    const fbq: FbqFn = function (...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, args);
      else (fbq.queue as unknown[]).push(args);
    };
    fbq.queue = [];
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
    const t = document.createElement("script");
    t.async = true;
    t.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(t);
  }
  // Hold all pings until consent is explicitly granted below, so the
  // automatic PageView never fires before the grant signal.
  window.fbq("consent", "revoke");
  window.fbq("init", META_PIXEL_ID);
  window.fbq("consent", "grant");
  window.fbq("track", "PageView");
  state.metaLoaded = true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Off-interaction send scheduler (INP — task #7081).
//
// track() is called synchronously inside click handlers (book CTAs, search
// cards, support links). Fanning out to PostHog + gtag + fbq inside the
// interaction task inflates INP, so sends are appended to a FIFO and drained
// in a macrotask (setTimeout 0) — AFTER the interaction task has yielded and
// the browser has had a chance to paint. A single FIFO preserves event
// ordering across all destinations; props are still merged (UTM/first-touch)
// synchronously at call time so deferred delivery never changes payloads.
// ─────────────────────────────────────────────────────────────────────────────
const deferredSends: Array<() => void> = [];
let deferredFlushScheduled = false;

function drainDeferredSends() {
  deferredFlushScheduled = false;
  while (deferredSends.length) {
    const fn = deferredSends.shift();
    try { fn?.(); } catch {}
  }
}

function deferSend(fn: () => void) {
  deferredSends.push(fn);
  if (deferredFlushScheduled) return;
  deferredFlushScheduled = true;
  setTimeout(drainDeferredSends, 0);
}

function flushQueue() {
  // Route the pre-consent buffer through the same deferred FIFO so a consent
  // grant (also a click) doesn't burst every buffered SDK call inside the
  // banner's interaction task.
  while (state.queue.length) {
    const fn = state.queue.shift();
    if (fn) deferSend(fn);
  }
}

async function applyConsent(consent: ConsentState) {
  pushConsentMode(consent);
  // PostHog is the product-analytics + session-replay tool → analytics only.
  if (consent.analytics) {
    await loadPostHog(consent);
    // Consent may have changed while the SDK was loading.
    if (getActivePlatform() === "bac" && !getConsent().analytics) return applyConsent(getConsent());
    // Upgrade pre-consent (memory) instance to durable persistence so
    // returning visitors merge into the same distinct_id and the
    // default dashboards (Retention, Growth accounting, etc.) work.
    try { state.posthog?.set_config({ persistence: "localStorage+cookie" }); } catch {}
    // If PostHog was previously opted out (revoke → re-grant), re-enable it.
    // Without this call, `opt_out_capturing()` persists in localStorage and
    // no events will be captured even after the user re-consents.
    try { state.posthog?.opt_in_capturing(); } catch {}
    // Consent may arrive after the lazy SDK's loaded callback. Do not wait
    // for another route change to start an eligible, consented session.
    if (state.posthog) startCurrentConsentedReplay(state.posthog);
    // Identify here in case loadPostHog() ran pre-consent (and so
    // skipped the identify in its `loaded` callback).
    if (state.userId) {
      try { if (state.posthog) identifyPostHog(state.posthog, state.userId, state.traits ?? undefined); } catch {}
    }
  } else if (hasChosen()) {
    // Explicit reject (banner dismissed with Reject All / analytics
    // toggled off). Stop capturing entirely.
    try { state.posthog?.opt_out_capturing(); } catch {}
    try { state.posthog?.stopSessionRecording(); } catch {}
  } else {
    // Pre-consent: keep PostHog running in memory-persistence mode so
    // pageviews still flow without dropping cookies. Boot it now if
    // it isn't already loaded.
    await loadPostHog(consent);
  }
  // GA4 runs in Consent Mode v2 ADVANCED: configure it on every call,
  // including pre-consent. pushConsentMode (above) keeps the storage grants
  // current, so pre-consent it sends cookieless pings and post-consent it
  // sends full cookie-backed measurement. This is what lets GA4 count live
  // users the same way PostHog does, instead of staying blank until consent.
  loadGA4(consent);
  // Google Ads uses Consent Mode v2 advanced mode too: configure the destination
  // on page load, while ad_storage remains denied until marketing is accepted.
  loadGoogleAds(consent);
  // Meta Pixel has no equivalent cookieless consent mode and remains dormant
  // unless marketing consent is granted.
  if (consent.marketing) {
    loadMetaPixel(consent);
    // Re-grant Meta consent on a revoke → re-grant cycle (Google Ads is
    // already re-granted via Consent Mode in pushConsentMode above).
    try { window.fbq?.("consent", "grant"); } catch {}
  } else if (state.metaLoaded) {
    // Marketing withdrawn after a prior grant: stop the pixel from sending.
    try { window.fbq?.("consent", "revoke"); } catch {}
  }
  // GA4 (cookieless) and PostHog (memory mode) both load pre-consent now, so
  // flush any buffered events regardless of consent — they fan out to the
  // already-loaded destinations and respect each SDK's consent state.
  flushQueue();
}

export function init(releaseReason: AnalyticsReleaseReason = "interaction"): void {
  if (
    state.initialized ||
    typeof window === "undefined" ||
    !isProductionAnalyticsEnabled()
  )
    return;
  state.initialized = true;
  state.releaseReason = releaseReason;
  if (releaseReason === "dwell") {
    // Dwell starts anonymous measurement only. The startup scheduler has
    // already removed its listeners, so retain a one-shot real-interaction
    // bridge for the visitor who subsequently uses this same eligible page.
    const activate = () => {
      window.removeEventListener("pointerdown", activate);
      window.removeEventListener("keydown", activate);
      window.removeEventListener("touchstart", activate);
      state.releaseReason = "interaction";
      if (state.posthog) startCurrentConsentedReplay(state.posthog);
    };
    window.addEventListener("pointerdown", activate, { once: true, passive: true });
    window.addEventListener("keydown", activate, { once: true });
    window.addEventListener("touchstart", activate, { once: true, passive: true });
  }
  captureFirstTouch();
  ensureConsentModeDefault();
  const consent = getConsent();
  void applyConsent(consent);
  subscribeConsent((c) => { void applyConsent(c); });
  // Real-user Core Web Vitals (task #7086). Observers must be registered
  // early (they use buffered PerformanceObservers); actual sending routes
  // through the shared PostHog instance below, so it inherits exactly the
  // same consent behaviour as pageview capture: memory-mode (cookieless)
  // pre-consent, durable post-consent, and a hard no-op after an explicit
  // reject (opt_out_capturing makes capture() a no-op). Dynamically imported
  // so the ~7 KB web-vitals module lands in its own async chunk instead of
  // growing the shared critical-path bundle (which sits at the 2 MB budget);
  // web-vitals uses buffered PerformanceObservers, so registering after the
  // async import still observes the page's LCP.
  void import("./web-vitals")
    .then((m) => m.initWebVitals(captureWebVital))
    .catch(() => {});
}

// PostHog-only capture used by the web-vitals reporter. The web-vitals
// library reports final values on pageleave/bfcache-restore — often before
// the async posthog-js import resolves on short visits — so events fired
// before the SDK loads are pushed onto the shared queue that applyConsent()
// flushes once PostHog is up. GA4/ads never receive these (product
// diagnostics only, keeps ad payloads clean).
function captureWebVital(props: Record<string, unknown>): void {
  if (!isProductionAnalyticsEnabled()) return;
  const doSend = () => {
    try { state.posthog?.capture("web_vitals", props); } catch {}
  };
  if (state.phLoaded) doSend();
  else state.queue.push(doSend);
}

function mergeSuperProps<T extends Record<string, unknown>>(props: T): T & Record<string, unknown> {
  const ft = getFirstTouch();
  const utm = getCurrentUtm();
  const out: Record<string, unknown> = { ...props };
  if (ft) {
    if (ft.utm_source) out.first_touch_utm_source = ft.utm_source;
    if (ft.utm_medium) out.first_touch_utm_medium = ft.utm_medium;
    if (ft.utm_campaign) out.first_touch_utm_campaign = ft.utm_campaign;
    if (ft.utm_content) out.first_touch_utm_content = ft.utm_content;
    if (ft.utm_term) out.first_touch_utm_term = ft.utm_term;
    if (ft.gclid) out.first_touch_gclid = ft.gclid;
    if (ft.referrer) out.first_touch_referrer = ft.referrer;
    if (ft.landing_path) out.first_touch_landing_path = ft.landing_path;
  }
  Object.assign(out, utm);
  return redactAnalyticsProps(out) as T & Record<string, unknown>;
}

const GA4_ALIASES: Partial<Record<EventName, string>> = {
  search: "search",
  car_viewed: "view_item",
  dates_selected: "add_to_cart",
  checkout_started: "begin_checkout",
  payment_info_entered: "add_payment_info",
  booking_confirmed: "purchase",
  refund_issued: "refund",
};

// Meta Pixel standard-event names for the funnel events we track. Mirrors
// the GA4 alias map so both ad platforms see the same conversions.
//
// InitiateCheckout fires on `booking_requested` (the submit step) rather than
// `checkout_started` (page load) because the server-side Conversions API
// mirror is emitted from the same submit and de-duplicated via the trip's
// public id (`checkout_<public_id>`) — only `booking_requested` carries that
// id on the browser. Likewise `booking_confirmed` → Purchase de-dups via
// `purchase_<public_id>`. See `metaEventIdFor` + lib/meta-capi.ts on the API.
const META_ALIASES: Partial<Record<EventName, string>> = {
  search: "Search",
  car_viewed: "ViewContent",
  dates_selected: "AddToCart",
  booking_requested: "InitiateCheckout",
  payment_info_entered: "AddPaymentInfo",
  booking_confirmed: "Purchase",
};

// Shared de-duplication id for events the server also emits via the Meta
// Conversions API. MUST match the server's `event_id` exactly (keyed on the
// trip's public id) or Meta double-counts the browser + server copies.
function metaEventIdFor(event: EventName, merged: Record<string, unknown>): string | undefined {
  if (event === "booking_confirmed") {
    const id = merged.trip_id ?? merged.transaction_id;
    return id != null ? `purchase_${String(id)}` : undefined;
  }
  if (event === "booking_requested") {
    const id = merged.trip_id;
    return id != null ? `checkout_${String(id)}` : undefined;
  }
  return undefined;
}

// Google Ads conversion labels per funnel event. Each conversion only fires
// when BOTH VITE_GOOGLE_ADS_ID and the event's label env var are configured.
function googleAdsLabelFor(event: EventName): string | undefined {
  if (event === "checkout_started") return GOOGLE_ADS_BEGIN_CHECKOUT_LABEL;
  if (event === "booking_confirmed") return GOOGLE_ADS_PURCHASE_LABEL;
  // Task #7874 — primary conversion: payment genuinely captured. Kept as a
  // SEPARATE conversion action from `booking_confirmed` (which also fires for
  // approved request bookings whose card is only authorised/scheduled), so
  // Ads can bid on real revenue without double-counting: each action has its
  // own label and each fires at most once per trip (see the transaction-id
  // dedupe in track()).
  if (event === "payment_succeeded") return GOOGLE_ADS_PAYMENT_LABEL;
  // Task #7874 — secondary conversion: booking-request submission. The legacy
  // POST /bookings path emits `booking_requested`; the PFC checkout path emits
  // `checkout_session_created` at the same funnel step. Both map to ONE
  // conversion action; a given renter journey only ever traverses one path.
  if (event === "booking_requested" || event === "checkout_session_created")
    return GOOGLE_ADS_BOOKING_REQUEST_LABEL;
  return undefined;
}

// Task #7874 — client-side once-per-trip guard for Ads conversion beacons.
// Google Ads itself de-dupes on transaction_id when the conversion action is
// set to "One" per click, but "Every" actions would double-count on reloads
// of the confirmation page. Fail-open: if storage is unavailable we still
// fire (Ads-side transaction_id de-dupe is the backstop).
const ADS_CONV_DEDUPE_PREFIX = "bac_adsconv_v1:";
function adsConversionAlreadySent(event: EventName, txId: string): boolean {
  try {
    return localStorage.getItem(`${ADS_CONV_DEDUPE_PREFIX}${event}:${txId}`) != null;
  } catch {
    return false;
  }
}
function markAdsConversionSent(event: EventName, txId: string): void {
  try {
    localStorage.setItem(`${ADS_CONV_DEDUPE_PREFIX}${event}:${txId}`, String(Date.now()));
  } catch {}
}

// Task #7874 — enhanced conversions. Google accepts pre-hashed PII under
// `user_data.sha256_email_address` (SHA-256 hex of the trim().toLowerCase()
// normalised email — the SAME normalisation the server-side Meta CAPI lib
// uses, see api-server/src/lib/meta-capi.ts). Set once per session under
// marketing consent; gtag attaches it to subsequent conversion pings.
async function sha256Hex(value: string): Promise<string | null> {
  try {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) return null;
    const digest = await subtle.digest("SHA-256", new TextEncoder().encode(value));
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

let hashedEmail: string | null = null;
let adsUserDataSet = false;

function maybeSetAdsUserData(): void {
  if (adsUserDataSet || !hashedEmail || !state.googleAdsLoaded) return;
  if (!getConsent().marketing) return;
  try {
    gtag("set", "user_data", { sha256_email_address: hashedEmail });
    adsUserDataSet = true;
  } catch {}
}

// Exposed for identify() below; recomputes lazily and is a no-op when the
// email is absent, WebCrypto is unavailable (non-secure context), or consent
// / configuration gates fail inside maybeSetAdsUserData().
function setEnhancedConversionEmail(email: string | undefined): void {
  const norm = (email ?? "").trim().toLowerCase();
  if (!norm) return;
  void sha256Hex(norm).then((hex) => {
    if (!hex) return;
    if (hashedEmail !== hex) adsUserDataSet = false;
    hashedEmail = hex;
    maybeSetAdsUserData();
  });
}

// Meta expects ecommerce params under specific keys. Map our shared
// value/currency/id props onto them without leaking the rest of the payload.
function toMetaParams(merged: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (typeof merged.value === "number") out.value = merged.value;
  if (typeof merged.currency === "string") out.currency = merged.currency;
  const contentId = merged.car_id ?? merged.trip_id;
  if (contentId != null) {
    out.content_ids = [String(contentId)];
    out.content_type = "product";
  }
  return out;
}

export function sanitizeCheckoutFunnelProps(props: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(props).filter(([key]) =>
    !/(address|message|token|secret|email|phone|access.*key|access.*token|payment.*id|client.*secret|provider.*error|raw.*error)/i.test(key)));
}

export function track<K extends EventName>(event: K, props: EventProps<K>): void {
  if (typeof window === "undefined" || !isProductionAnalyticsEnabled()) return;
  const checkoutFunnelEvent = event === "checkout_started"
    || event === "checkout_cta_clicked"
    || event === "checkout_cta_outcome"
    || event === "checkout_time_validation"
    || event === "checkout_payment_lifecycle"
    || event === "checkout_wallet_lifecycle"
    || event === "checkout_prerequisite_lifecycle"
    || event === "checkout_auth_handoff"
    || event === "checkout_identity"
    || event === "date_range_rejected";
  const safeProps = checkoutFunnelEvent
    ? sanitizeCheckoutFunnelProps(props as Record<string, unknown>)
    : props as Record<string, unknown>;
  const merged = mergeSuperProps(safeProps);
  const consent = getConsent();
  // Drop silently when neither bucket granted — we never buffer pre-consent.
  if (!consent.analytics && !consent.marketing) return;

  const doSend = () => {
    if (consent.analytics) {
      try {
        state.posthog?.capture(event, merged);
      } catch {}
    }
    if (consent.analytics || consent.marketing) {
      try {
        const gaEvent = GA4_ALIASES[event] || event;
        window.gtag?.("event", gaEvent, merged);
      } catch {}
    }
    // Paid-ads pixels are marketing-only. They only ran loadGoogleAds/
    // loadMetaPixel when marketing was granted, so metaLoaded/googleAdsLoaded
    // double as the "configured + consented" guard here.
    if (consent.marketing) {
      if (state.metaLoaded) {
        try {
          const metaEvent = META_ALIASES[event];
          if (metaEvent) {
            // Pass the shared `eventID` so Meta de-dups this browser pixel
            // event against the server-side Conversions API copy.
            const eventID = metaEventIdFor(event, merged);
            if (eventID) window.fbq?.("track", metaEvent, toMetaParams(merged), { eventID });
            else window.fbq?.("track", metaEvent, toMetaParams(merged));
          }
        } catch {}
      }
      if (state.googleAdsLoaded && GOOGLE_ADS_ID) {
        try {
          const label = googleAdsLabelFor(event);
          // Conversion identity: explicit transaction_id, else trip id, else
          // the PFC checkout-session public id (`checkout_session_created`
          // has no trip yet — the session id is its unique per-attempt key).
          const rawTxId = merged.transaction_id ?? merged.trip_id ?? merged.session_id;
          const txId = rawTxId != null ? String(rawTxId) : undefined;
          // Task #7874 — once-per-trip client-side dedupe (reload of the
          // confirmation page must not re-fire the conversion beacon).
          if (label && !(txId && adsConversionAlreadySent(event, txId))) {
            // Attach hashed-email user_data (enhanced conversions) if it has
            // become available since Ads loaded.
            maybeSetAdsUserData();
            window.gtag?.("event", "conversion", {
              send_to: `${GOOGLE_ADS_ID}/${label}`,
              ...(typeof merged.value === "number" ? { value: merged.value } : {}),
              ...(typeof merged.currency === "string" ? { currency: merged.currency } : {}),
              ...(txId ? { transaction_id: txId } : {}),
            });
            if (txId) markAdsConversionSent(event, txId);
          }
        } catch {}
      }
    }
  };

  // Buffer events fired before any destination SDK has loaded; flushQueue()
  // drains them once consent loads a destination. Loaded destinations get the
  // send DEFERRED off the interaction task (see deferSend) so click handlers
  // return before any SDK work runs.
  if (!state.phLoaded && !state.gaLoaded && !state.metaLoaded && !state.googleAdsLoaded) {
    state.queue.push(doSend);
  } else {
    deferSend(doSend);
  }
}

export function page(path?: string, title?: string): void {
  if (typeof window === "undefined" || !isProductionAnalyticsEnabled()) return;
  const p = sanitizeCheckoutUrl(path ?? window.location.pathname);
  // PostHog emits `$pageview` natively (see `capture_pageview: true` in
  // `loadPostHog`), so we no longer fire a custom `page_view` event for it
  // here — that double-counted views and starved the default dashboards
  // and scroll-depth tracker, which key off `$pageview`/`$pageleave`.
  // GA4 still gets its own `page_view` below since GA's funnels rely on it.
  if (GA_MEASUREMENT_ID) {
    const pageTitle = title ?? document.title;
    const pageLocation = sanitizeCheckoutUrl(window.location.href);
    if (state.gaLoaded) {
      // GA is configured — emit straight away.
      emitGaPageView(p, pageTitle, pageLocation);
    } else {
      // GA4 hasn't run `gtag("config", ...)` yet. Emitting now would queue
      // the event ahead of config in the dataLayer and gtag.js would drop
      // it (no stream configured). Stash the latest view; loadGA4() replays
      // it once config has run, so even a landing-only session is counted.
      state.pendingGaPageView = { path: p, title: pageTitle, location: pageLocation };
    }
  }
  // Toggle session replay per-route: only on the pages we care about.
  try {
    const ph = state.posthog;
    if (ph) {
      const pathname = new URL(p, window.location.origin).pathname;
      if (pathEligibleForReplay(pathname)) startCurrentConsentedReplay(ph, pathname);
      else ph.stopSessionRecording();
    }
  } catch {}
}

function identifyPostHog(ph: Pick<PostHog, "identify" | "reset">, userId: string, traits?: IdentifyTraits): void {
  if (state.pendingPosthogReset) {
    ph.reset();
    state.pendingPosthogReset = false;
  }
  ph.identify(userId, traits);
}

export function identify(userId: string, traits?: IdentifyTraits): void {
  if (!isProductionAnalyticsEnabled()) return;
  // A direct account switch must not merge the departing person into the next
  // account. Anonymous -> login deliberately does NOT reset.
  if (getActivePlatform() === "bac" && state.userId && state.userId !== userId) {
    // Do not generate a fresh tracking identifier while consent is denied.
    state.pendingPosthogReset = true;
  }
  state.userId = userId;
  state.traits = traits ?? null;
  // Task #7874 — enhanced conversions: stash the SHA-256 of the normalised
  // email. It is only ever SENT under marketing consent + a loaded Ads tag
  // (see maybeSetAdsUserData); hashing up-front costs nothing and lets a
  // later consent grant attach it immediately.
  setEnhancedConversionEmail(traits?.email);
  const consent = getConsent();
  // PostHog only when analytics consent is granted.
  if (consent.analytics) {
    try {
      if (state.posthog) identifyPostHog(state.posthog, userId, traits);
    } catch {}
  }
  // GA4 user_id whenever GA is active (analytics OR marketing) so that
  // server-side stitching via Measurement Protocol lines up with the
  // browser session.
  if (consent.analytics || consent.marketing) {
    try {
      if (GA_MEASUREMENT_ID) {
        window.gtag?.("config", GA_MEASUREMENT_ID, { user_id: userId });
        if (traits) window.gtag?.("set", "user_properties", traits);
      }
    } catch {}
  }
}

export function reset(): void {
  state.userId = null;
  state.traits = null;
  state.pendingPosthogReset = false;
  try { state.posthog?.reset(); } catch {}
  try {
    if (GA_MEASUREMENT_ID) window.gtag?.("config", GA_MEASUREMENT_ID, { user_id: undefined });
  } catch {}
}

export function setConsent(next: { analytics: boolean; marketing: boolean }): void {
  // Import lazily to avoid circular dep at module-eval time.
  import("./consent").then(({ setConsent: setC }) => { setC(next); });
}

export function getDistinctId(): string | null {
  try { return state.posthog?.get_distinct_id?.() ?? null; } catch { return null; }
}

export function getBookingAnalyticsContext(): BookingAnalyticsContext | undefined {
  if (getActivePlatform() !== "bac") return undefined;
  const consent = getConsent();
  if (!consent.analytics) {
    return { version: 1, consent: hasChosen() ? "denied" : "pending" };
  }
  // No fallback to state.userId, localStorage or a retained anonymous ID.
  const id = isProductionAnalyticsEnabled() && !isAutomatedBrowser()
    ? getDistinctId() : null;
  return bookingAnalyticsContextFromSdkId(id);
}

export { type EventName, type EventProps, type IdentifyTraits } from "./events";
